import { useRef, useState, type ReactElement } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { RecordingDock } from '@/components/atoms/recording-dock';
import { PlaybackDock } from '@/components/atoms/playback-dock';
import { MicPermissionCard } from '@/components/molecules/mic-permission-card';
import { FloatingRecorderPill } from '@/components/molecules/floating-recorder-pill';
import {
  TakeReviewSurface,
  type TakeReviewSavePayload,
} from '@/components/molecules/take-review-surface';
import { useRecorder, type Take } from '@/lib/hooks/use-recorder';
import { usePlayback } from '@/lib/hooks/use-playback';

export const Route = createFileRoute('/dev-takes-fixtures')({
  component: DevTakesFixturesRoute,
});

type SavedTake = TakeReviewSavePayload & {
  durationMs: number;
};

function DevTakesFixturesRoute(): ReactElement {
  const recorder = useRecorder();
  const audioRef = useRef<HTMLAudioElement>(null);
  const playback = usePlayback(audioRef);
  const [permissionDismissed, setPermissionDismissed] = useState<boolean>(false);
  const [navigatedAway, setNavigatedAway] = useState<boolean>(false);
  const [savedTakes, setSavedTakes] = useState<SavedTake[]>([]);
  const [fixturePlaybackMs, setFixturePlaybackMs] = useState<number>(0);

  const handleAllow = (): void => {
    setPermissionDismissed(true);
    void recorder.start();
  };

  const handleSaveTake = (payload: TakeReviewSavePayload): void => {
    const take = recorder.take as Take | null;
    if (take) {
      setSavedTakes((prev) => [...prev, { ...payload, durationMs: take.durationMs }]);
    }
    recorder.reset();
  };

  const showPermissionCard = !permissionDismissed && recorder.status === 'idle';
  const showRecordingDock = recorder.status === 'recording' || recorder.status === 'paused';
  const showReviewSurface = recorder.status === 'review';
  const showFloatingPill =
    navigatedAway && (recorder.status === 'recording' || recorder.status === 'paused');

  return (
    <main className="min-h-screen bg-[var(--bg)] p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-[var(--ink)]">Takes — dock fixtures</h1>
        <p className="mt-1 text-sm text-[var(--ink-3)]">
          Dev-only sandbox for the take recording / playback chrome. No backend wiring.
        </p>
      </header>

      <section
        aria-label="Recorder state"
        className="mb-6 flex flex-wrap items-center gap-4 rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-4"
      >
        <output aria-label="Recorder status" className="font-mono text-sm">
          status: {recorder.status}
        </output>
        <output aria-label="Recorded takes count" className="font-mono text-sm">
          saved: {savedTakes.length}
        </output>
        <button
          type="button"
          onClick={handleAllow}
          className="inline-flex h-9 items-center justify-center rounded-[var(--r-sm)] bg-[var(--ink)] px-3 text-xs font-semibold text-[var(--bg-surface)] hover:bg-[var(--brand-ink)]"
        >
          Start recording
        </button>
        <button
          type="button"
          onClick={() => recorder.reset()}
          className="inline-flex h-9 items-center justify-center rounded-[var(--r-sm)] bg-[var(--bg-soft)] px-3 text-xs font-semibold text-[var(--ink)] hover:bg-[var(--rule)]"
        >
          Reset
        </button>
        <label className="inline-flex items-center gap-2 text-sm text-[var(--ink-2)]">
          <input
            type="checkbox"
            checked={navigatedAway}
            onChange={(event) => setNavigatedAway(event.target.checked)}
          />
          Simulate navigation away
        </label>
      </section>

      {showPermissionCard && (
        <section aria-label="Permission card" className="mb-6">
          <MicPermissionCard onAllow={handleAllow} />
        </section>
      )}

      <section
        aria-label="Playback fixture"
        className="mb-24 rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-4"
      >
        <h2 className="mb-3 text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">
          Playback fixture (silent test source)
        </h2>
        <audio
          ref={audioRef}
          aria-label="fixture-audio"
          src="data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA="
          preload="metadata"
        />
        <PlaybackDock
          status={playback.status === 'playing' ? 'playing' : 'paused'}
          title="Sample take"
          caption="Fixture"
          currentMs={Math.max(playback.currentMs, fixturePlaybackMs)}
          durationMs={Math.max(playback.durationMs, 10000)}
          onPlay={() => void playback.play()}
          onPause={() => playback.pause()}
          onSeek={(ms) => {
            setFixturePlaybackMs(ms);
            playback.seekTo(ms);
          }}
        />
      </section>

      {showRecordingDock && (
        <RecordingDock
          status={recorder.status === 'paused' ? 'paused' : 'recording'}
          elapsedMs={recorder.elapsedMs}
          waveformAmplitudes={recorder.waveformAmplitudes}
          onStop={() => recorder.stop()}
          onPause={() => recorder.pause()}
          onResume={() => recorder.resume()}
          onCancel={() => recorder.cancel()}
        />
      )}

      <TakeReviewSurface
        open={showReviewSurface}
        take={recorder.take}
        onSave={handleSaveTake}
        onDiscard={() => recorder.reset()}
        onRetake={() => {
          recorder.reset();
          void recorder.start();
        }}
        onClose={() => recorder.reset()}
      />

      {showFloatingPill && (
        <FloatingRecorderPill
          status={recorder.status === 'paused' ? 'paused' : 'recording'}
          elapsedMs={recorder.elapsedMs}
          onReturn={() => setNavigatedAway(false)}
        />
      )}
    </main>
  );
}
